import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";

const COLORS = ["#6c63ff", "#00d68f", "#ffd166", "#ff5c6c", "#a78bfa", "#34d399", "#f59e0b", "#ec4899", "#60a5fa", "#f97316"];

function StatCard({ label, value, sub, color }) {
  return (
    <div className="card" style={{ ...styles.statCard, borderColor: color ? `${color}33` : "var(--border)" }}>
      {color && <div style={{ ...styles.statAccent, background: color }} />}
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: color || "var(--text-primary)" }}>{value}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}

function PartnerRow({ partner, total, index }) {
  const pct = total > 0 ? ((partner.totalStake / total) * 100).toFixed(2) : "0.00";
  const color = COLORS[index % COLORS.length];
  return (
    <div style={styles.partnerRow}>
      <div style={{ ...styles.colorDot, background: color }} />
      <div style={styles.partnerName}>{partner.name}</div>
      <div style={styles.partnerEmail}>{partner.email}</div>
      <div style={styles.partnerStake}>${partner.totalStake?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
      <div style={styles.partnerPct}>
        <div style={styles.pctBar}>
          <div style={{ ...styles.pctFill, width: `${pct}%`, background: color }} />
        </div>
        <span style={{ color, fontWeight: 700, fontSize: "14px", minWidth: "52px", textAlign: "right" }}>{pct}%</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cutInDate, setCutInDate] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const snap = await getDocs(collection(db, "partners"));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPartners(data);

        const configDoc = await getDoc(doc(db, "config", "settings"));
        if (configDoc.exists()) {
          setCutInDate(configDoc.data().cutInDate || "");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const totalInvested = partners.reduce((sum, p) => sum + (p.totalStake || 0), 0);
  const partnerCount = partners.length;
  const topPartner = [...partners].sort((a, b) => (b.totalStake || 0) - (a.totalStake || 0))[0];

  const pieData = partners.map((p, i) => ({
    name: p.name,
    value: p.totalStake || 0,
    color: COLORS[i % COLORS.length],
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const d = payload[0];
      const pct = totalInvested > 0 ? ((d.value / totalInvested) * 100).toFixed(2) : "0.00";
      return (
        <div style={styles.tooltip}>
          <div style={{ fontWeight: 700, color: d.payload.color, marginBottom: "4px" }}>{d.name}</div>
          <div style={{ color: "var(--text-primary)", fontSize: "15px", fontWeight: 600 }}>
            ${d.value.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </div>
          <div style={{ color: "var(--text-secondary)", fontSize: "13px" }}>{pct}% stake</div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingSpinner} />
        <div style={{ color: "var(--text-secondary)", marginTop: "16px" }}>Loading partner data...</div>
      </div>
    );
  }

  return (
    <div style={styles.page} className="animate-in">
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.greeting}>Welcome back, {currentUser?.displayName?.split(" ")[0]} 👋</div>
          <h1 style={styles.title}>Partner Dashboard</h1>
          {cutInDate && (
            <div style={styles.cutInBadge}>
              📅 Data from: {new Date(cutInDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          )}
        </div>
        <div style={styles.lastUpdated}>
          Last updated: {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </div>

      {/* Stat cards */}
      <div style={styles.statsGrid}>
        <StatCard
          label="Total Invested"
          value={`$${totalInvested.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
          sub="Combined partner contributions"
          color="#6c63ff"
        />
        <StatCard
          label="Active Partners"
          value={partnerCount}
          sub="Registered stakeholders"
          color="#00d68f"
        />
        <StatCard
          label="Largest Stake"
          value={topPartner ? `${totalInvested > 0 ? ((topPartner.totalStake / totalInvested) * 100).toFixed(1) : 0}%` : "—"}
          sub={topPartner?.name || "—"}
          color="#ffd166"
        />
        <StatCard
          label="Avg. Contribution"
          value={partnerCount > 0 ? `$${(totalInvested / partnerCount).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "$0"}
          sub="Per partner"
          color="#ff5c6c"
        />
      </div>

      {/* Main content */}
      <div style={styles.mainGrid}>
        {/* Partner table */}
        <div className="card" style={styles.tableCard}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Partner Stakes</h2>
            <div style={styles.cardSub}>Current ownership breakdown</div>
          </div>
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>Partner</span>
            <span style={{ flex: 2, display: "flex", justifyContent: "flex-end" }}>Invested</span>
            <span style={{ flex: 3, display: "flex", justifyContent: "flex-end" }}>Ownership %</span>
          </div>
          {partners.length === 0 ? (
            <div style={styles.empty}>No partners found. Add partners via the Admin Panel.</div>
          ) : (
            partners
              .sort((a, b) => (b.totalStake || 0) - (a.totalStake || 0))
              .map((p, i) => (
                <PartnerRow key={p.id} partner={p} total={totalInvested} index={i} />
              ))
          )}
        </div>

        {/* Pie chart */}
        <div className="card" style={styles.chartCard}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Ownership Distribution</h2>
            <div style={styles.cardSub}>Visual stake breakdown</div>
          </div>
          {partners.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} stroke="var(--bg-card)" strokeWidth={2} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(value) => <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.empty}>No data to display</div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { maxWidth: "1200px", margin: "0 auto" },
  loading: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    minHeight: "400px",
  },
  loadingSpinner: {
    width: "40px", height: "40px",
    border: "3px solid var(--border)",
    borderTop: "3px solid var(--accent)",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
    marginBottom: "32px", flexWrap: "wrap", gap: "12px",
  },
  greeting: { fontSize: "14px", color: "var(--text-muted)", marginBottom: "4px" },
  title: { fontSize: "28px", fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--text-primary)" },
  cutInBadge: {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "var(--accent-dim)", border: "1px solid var(--border-accent)",
    borderRadius: "100px", padding: "3px 12px", fontSize: "12px",
    color: "var(--accent-bright)", marginTop: "8px",
  },
  lastUpdated: { fontSize: "13px", color: "var(--text-muted)", alignSelf: "flex-end" },
  statsGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px", marginBottom: "24px",
  },
  statCard: {
    position: "relative", overflow: "hidden", padding: "20px 24px",
  },
  statAccent: {
    position: "absolute", top: 0, left: 0, right: 0, height: "2px",
  },
  statLabel: { fontSize: "12px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "8px" },
  statValue: { fontSize: "26px", fontFamily: "var(--font-display)", fontWeight: 800, marginBottom: "4px" },
  statSub: { fontSize: "12px", color: "var(--text-muted)" },
  mainGrid: {
    display: "grid", gridTemplateColumns: "1fr 380px", gap: "20px",
    alignItems: "start",
  },
  tableCard: { padding: "24px" },
  chartCard: { padding: "24px" },
  cardHeader: { marginBottom: "20px" },
  cardTitle: { fontSize: "16px", fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text-primary)" },
  cardSub: { fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" },
  tableHeader: {
    display: "flex", gap: "12px", padding: "8px 0",
    borderBottom: "1px solid var(--border)", marginBottom: "4px",
    fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em",
  },
  partnerRow: {
    display: "flex", alignItems: "center", gap: "12px",
    padding: "14px 0", borderBottom: "1px solid var(--border)",
  },
  colorDot: { width: "10px", height: "10px", borderRadius: "50%", flexShrink: 0 },
  partnerName: { flex: 1, fontWeight: 600, fontSize: "14px", color: "var(--text-primary)" },
  partnerEmail: { flex: 1, fontSize: "12px", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  partnerStake: { flex: 1, fontWeight: 700, fontSize: "14px", color: "var(--text-primary)", textAlign: "right" },
  partnerPct: { flex: 2, display: "flex", alignItems: "center", gap: "10px", justifyContent: "flex-end" },
  pctBar: {
    flex: 1, height: "6px", background: "var(--bg-elevated)",
    borderRadius: "3px", overflow: "hidden", maxWidth: "80px",
  },
  pctFill: { height: "100%", borderRadius: "3px", transition: "width 0.6s ease" },
  empty: { color: "var(--text-muted)", textAlign: "center", padding: "40px 0", fontSize: "14px" },
  tooltip: {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)", padding: "12px 16px",
  },
};
